/**
 * پیش‌نمایشِ PDF داخلِ خودِ صفحه — قبل از دانلود/چاپ.
 *
 * خواستهٔ کاربر (۱۴۰۵/۰۶/۱۸): «اول یه پریویو تو همون قسمت به‌صورت پاپ‌آپ نشون بده،
 * ببینیم چی به چیه، شاید بخوایم ویرایش کنیم؛ بعد بریم سراغ پرینت یا دانلود.» و بعد:
 * «بین نفرات جابه‌جا بشیم، عقب‌جلو، اون بالا سرچ کنیم؛ سایزِ چاپ (A4/A5) رو هم قبلش
 * انتخاب کنیم، با پیش‌فرضِ مشخص.»
 *
 * PDF با هدرِ احراز هویت به‌شکلِ blob گرفته می‌شود و در `<iframe>` (نمایشگرِ خودِ
 * مرورگر) نمایش داده می‌شود. دو حالت:
 *   • تکی:   { url, title, fallbackName, onEdit }
 *   • لیستی: { items, index, resolve(item) → همان چهارتا, labelOf(item) }  ← قبلی/بعدی + جست‌وجو
 * اندازهٔ کاغذ به‌عنوانِ `?page=A4|A5` به سرور می‌رود و آخرین انتخاب per `storageKey`
 * در همین مرورگر می‌ماند.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal, Segmented, Select, Space, Spin, Tooltip, Typography, message } from "antd";
import {
  DownloadOutlined, EditOutlined, ExportOutlined, LeftOutlined, PrinterOutlined,
  ReloadOutlined, RightOutlined,
} from "@ant-design/icons";
import Alert from "./GxAlert";
import api from "../api/client";
import { apiError } from "../utils/apiError";
import { filenameFromHeader, saveBlob } from "../utils/downloadFile";

const { Text } = Typography;
export const PAGE_SIZES = ["A4", "A5"];

const storeKey = (k) => `gx.pdfpage.${k || "default"}`;
const readPage = (k, def) => { try { return localStorage.getItem(storeKey(k)) || def; } catch { return def; } };
const savePage = (k, v) => { try { localStorage.setItem(storeKey(k), v); } catch { /* حافظهٔ مرورگر بسته است */ } };

const EMPTY = { loading: false, error: null, blob: null, objUrl: null, name: null };

export default function PdfPreviewModal({
  open, onClose,
  url, params, title = "پیش‌نمایش", fallbackName = "file.pdf", onEdit, editLabel = "ویرایش اطلاعات",
  items, index = 0, resolve, labelOf,
  pageSizes = PAGE_SIZES, defaultPage = "A4", storageKey,
  width = 960,
}) {
  const [idx, setIdx] = useState(index);
  const [page, setPage] = useState(() => readPage(storageKey, defaultPage));
  const [state, setState] = useState(EMPTY);
  const iframeRef = useRef(null);
  const total = items ? items.length : 0;
  const listMode = total > 0;

  // با هر بازشدن: نفرِ کلیک‌شده و اندازهٔ ذخیره‌شده — **همین رندر**، نه در effect؛
  // وگرنه اولین بارگذاری با نفرِ قبلی می‌رفت و بعد دوباره با نفرِ درست (دو درخواست).
  const wasOpen = useRef(false);
  if (open && !wasOpen.current) {
    wasOpen.current = true;
    const p0 = readPage(storageKey, defaultPage);
    if (idx !== index) setIdx(index);
    if (page !== p0) setPage(p0);
  } else if (!open && wasOpen.current) {
    wasOpen.current = false;
  }

  const cur = useMemo(() => {
    if (listMode) {
      const it = items[Math.min(Math.max(idx, 0), total - 1)];
      return { ...(resolve ? resolve(it) : {}), item: it };
    }
    return { url, title, fallbackName, onEdit, editLabel };
  }, [listMode, items, idx, total, resolve, url, title, fallbackName, onEdit, editLabel]);

  const reqParams = useMemo(
    () => ({ ...(params || {}), ...(cur.params || {}), page }),
    [params, cur.params, page]);
  const reqKey = JSON.stringify(reqParams);

  const load = async () => {
    if (!cur.url) return;
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const r = await api.get(cur.url, { params: reqParams, responseType: "blob" });
      const blob = r.data;
      const objUrl = URL.createObjectURL(blob);
      const name = filenameFromHeader(r.headers?.["content-disposition"]) || cur.fallbackName || fallbackName;
      setState((s) => {
        if (s.objUrl) URL.revokeObjectURL(s.objUrl);
        return { loading: false, error: null, blob, objUrl, name };
      });
    } catch (e) {
      // خطای سرور به‌شکلِ blob می‌آید؛ متنش را درمی‌آوریم
      let detail = null;
      try {
        const txt = await e?.response?.data?.text?.();
        detail = txt ? (JSON.parse(txt)?.detail || txt) : null;
      } catch { /* متنِ خطا خوانا نبود */ }
      /* 🐞 v2.112.3: detailِ بعضی خطاها شیء است ({code, message}) و مستقیم در Alert می‌رفت ⇒ React
         «Objects are not valid as a React child» و کلِ پنجره با «ارور» می‌افتاد. همیشه متن کن. */
      if (detail && typeof detail === "object") {
        detail = detail.message || (Array.isArray(detail) ? detail.map((x) => x?.msg || "").join("؛ ") : JSON.stringify(detail));
      }
      setState((s) => {
        if (s.objUrl) URL.revokeObjectURL(s.objUrl);
        return { ...EMPTY, error: detail || apiError(e, "خطا در ساختِ PDF") };
      });
    }
  };

  useEffect(() => {
    if (open && cur.url) load();
    if (!open) {
      setState((s) => { if (s.objUrl) URL.revokeObjectURL(s.objUrl); return EMPTY; });
    }
  }, [open, cur.url, reqKey]);   // eslint-disable-line

  // میان‌بر: Alt+← بعدی، Alt+→ قبلی (RTL) — مثلِ ویرایشگرِ نفر‌به‌نفر
  useEffect(() => {
    if (!open || !listMode) return undefined;
    const h = (e) => {
      if (!e.altKey) return;
      if (e.key === "ArrowLeft") { e.preventDefault(); go(1); }
      if (e.key === "ArrowRight") { e.preventDefault(); go(-1); }
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  });

  const go = (d) => setIdx((i) => Math.min(Math.max(i + d, 0), total - 1));

  const download = () => {
    if (!state.blob) return;
    saveBlob(state.blob, state.name || cur.fallbackName || fallbackName);
    message.success(`دانلود شد: ${state.name || cur.fallbackName || fallbackName}`);
  };

  const openTab = () => { if (state.objUrl) window.open(state.objUrl, "_blank", "noopener"); };

  const print = () => {
    // نمایشگرِ PDFِ مرورگر داخلِ iframe خودش چاپ می‌کند (Chrome/Firefox)؛
    // اگر نشد، تبِ جدید باز می‌شود تا از نمایشگرِ خودِ مرورگر چاپ شود.
    try {
      const w = iframeRef.current?.contentWindow;
      if (w && typeof w.print === "function") { w.focus(); w.print(); return; }
    } catch { /* مرورگر اجازه نداد */ }
    openTab();
  };

  const handleEdit = () => { onClose(); if (cur.onEdit) cur.onEdit(); };
  const changePage = (v) => { setPage(v); savePage(storageKey, v); };

  const labelFor = (it, i) => (labelOf ? labelOf(it)
    : it?.full_name || it?.employee_name || `#${i + 1}`);

  return (
    <Modal open={open} onCancel={onClose} width={width} footer={null} style={{ top: 16 }} destroyOnHidden
      title={listMode ? `${cur.title || "پیش‌نمایش"}  (${idx + 1} از ${total})` : (cur.title || "پیش‌نمایش")}>
      {/* نوارِ بالا: ناوبری بینِ نفرات + جست‌وجو + اندازهٔ کاغذ */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
        flexWrap: "wrap", marginBottom: 10 }}>
        <Space wrap>
          {listMode && (
            <>
              <Tooltip title="نفر قبلی (Alt+→)">
                <Button icon={<RightOutlined />} onClick={() => go(-1)} disabled={idx <= 0}>قبلی</Button>
              </Tooltip>
              <Select showSearch value={idx} onChange={setIdx} style={{ minWidth: 260 }}
                optionFilterProp="label" placeholder="جست‌وجوی نفر…"
                options={items.map((it, i) => ({ value: i, label: labelFor(it, i) }))} />
              <Tooltip title="نفر بعدی (Alt+←)">
                <Button icon={<LeftOutlined />} onClick={() => go(1)} disabled={idx >= total - 1}>بعدی</Button>
              </Tooltip>
            </>
          )}
        </Space>
        <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>اندازهٔ کاغذ</Text>
          <Segmented value={page} onChange={changePage}
            options={pageSizes.map((p) => ({ value: p, label: p }))} />
        </Space>
      </div>

      {state.error && <Alert type="error" showIcon style={{ marginBottom: 10 }} message={state.error} />}
      <div style={{
        position: "relative", height: "68vh", minHeight: 400, borderRadius: 10, overflow: "hidden",
        border: "1px solid var(--border,#e4e8f0)", background: "var(--surface-2,#f6f8fb)",
      }}>
        {state.loading && (
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center",
            justifyContent: "center", flexDirection: "column", gap: 10, zIndex: 1,
            background: "rgba(246,248,251,.6)" }}>
            <Spin size="large" />
            <Text type="secondary">در حال ساختِ PDF…</Text>
          </div>
        )}
        {state.objUrl && (
          <iframe ref={iframeRef} title={cur.title || "pdf"} src={state.objUrl}
            style={{ width: "100%", height: "100%", border: 0, display: "block" }} />
        )}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
        gap: 8, flexWrap: "wrap", marginTop: 12 }}>
        <Space wrap>
          <Button onClick={onClose}>بستن</Button>
          {cur.onEdit && (
            <Button icon={<EditOutlined />} onClick={handleEdit}>{cur.editLabel || editLabel}</Button>
          )}
          <Button icon={<ReloadOutlined />} onClick={load} loading={state.loading}>بازسازی</Button>
        </Space>
        <Space wrap>
          <Text type="secondary" style={{ fontSize: 12 }}>{state.name}{state.name ? ` • ${page}` : ""}</Text>
          <Button icon={<ExportOutlined />} onClick={openTab} disabled={!state.objUrl}>تب جدید</Button>
          <Button icon={<PrinterOutlined />} onClick={print} disabled={!state.objUrl}>چاپ</Button>
          <Button type="primary" icon={<DownloadOutlined />} onClick={download} disabled={!state.blob}>
            دانلود
          </Button>
        </Space>
      </div>
    </Modal>
  );
}
